import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
	Globe,
	Apple,
	Play,
	Scissors,
	UtensilsCrossed,
	ShoppingBag,
	Sparkles,
	ArrowRight,
	ExternalLink,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CookiesBanner from "../components/CookiesBanner";
import EliteEjemplo from "../assets/barber/elite-ejemplo.png";
import BarberiaMarcoEjemplo from "../assets/barber/barberia-marco-ejemplo.png";
import TempleFadeEjemplo from "../assets/barber/temple-fade-ejemplo.png";
import EssenzaEjemplo from "../assets/beauty/essenza-ejemplo.png";
import ViancaEjemplo from "../assets/beauty/vianca-ejemplo.png";
import LaMaisonEjemplo from "../assets/beauty/la-maison-ejemplo.png";
import MuseEjemplo from "../assets/beauty/muse-ejemplo.png";
import LaCantinaEjemplo from "../assets/restaurant/la-cantina-ejemplo.png";
import LaTavolaEjemplo from "../assets/restaurant/la-tavola-ejemplo.png";
import BrunchBlossomEjemplo from "../assets/restaurant/brunch-blossom-ejemplo.png";
import GrillhouseEjemplo from "../assets/restaurant/grillhouse-ejemplo.png";
import AureaEjemplo from "../assets/shops/aurea-ejemplo.png";
import LiaEjemplo from "../assets/shops/lia-ejemplo.png";
import PetsEjemplo from "../assets/shops/pets-ejemplo.png";

const fadeUp = {
	hidden: { opacity: 0, y: 30 },
	visible: (i = 0) => ({
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, delay: i * 0.12 },
	}),
};

const SCROLL_OFFSET = 110;

const CATEGORIES = [
	{
		id: "barbers",
		icon: Scissors,
		examples: [
			{
				name: "Elite",
				description: "barbers_elite",
				image: EliteEjemplo,
				webLink: "https://www.elitebarber.es/",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.elite",
				iosLink:
					"https://apps.apple.com/us/app/elite-studio/id6742740084",
				hasApp: true,
			},
			{
				name: "Barbería Marco",
				description: "barbers_marco",
				image: BarberiaMarcoEjemplo,
				webLink: "https://web.applaby.com/barberia-marco/",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.barberiamarco",
				iosLink:
					"https://apps.apple.com/us/app/la-barber%C3%ADa-de-marco/id6745147346",
				hasApp: true,
			},
			{
				name: "Temple Fade Studio",
				description: "barbers_temple",
				image: TempleFadeEjemplo,
				webLink: "https://www.templefadestudio.es/",
			},
		],
	},
	{
		id: "salons",
		icon: Sparkles,
		examples: [
			{
				name: "Essenza",
				description: "salons_essenza",
				image: EssenzaEjemplo,
				webLink: "https://web.applaby.com/essenza",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.essenza",
				iosLink:
					"https://apps.apple.com/us/app/essenza/id6745752145",
				hasApp: true,
			},
			{
				name: "Vianca",
				description: "salons_vianca",
				image: ViancaEjemplo,
				webLink: "https://web.applaby.com/vianca",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.vianca",
				iosLink:
					"https://apps.apple.com/us/app/vianca/id6744869023",
				hasApp: true,
			},
			{
				name: "La Maison",
				description: "salons_maison",
				image: LaMaisonEjemplo,
				webLink: "https://www.lamaisonstudio.es",
			},
			{
				name: "Muse Studio",
				description: "salons_muse",
				image: MuseEjemplo,
				webLink: "https://www.musesalon.es",
			},
		],
	},
	{
		id: "restaurants",
		icon: UtensilsCrossed,
		examples: [
			{
				name: "Grillhouse 27",
				description: "restaurants_grillhouse",
				image: GrillhouseEjemplo,
				webLink: "https://www.grillhouse27.com/",
			},
			{
				name: "La Cantina",
				description: "restaurants_cantina",
				image: LaCantinaEjemplo,
				webLink: "https://www.lacantinarestaurante.com/",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.lacantina",
				iosLink:
					"https://apps.apple.com/us/app/la-cantina/id6742712477",
				hasApp: true,
			},
			{
				name: "La Tavola",
				description: "restaurants_tavola",
				image: LaTavolaEjemplo,
				webLink: "https://www.latavolaitaliana.es/",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.latavola",
				iosLink:
					"https://apps.apple.com/us/app/la-tavola/id6744865943",
				hasApp: true,
			},
			{
				name: "Brunch & Blossom",
				description: "restaurants_brunch",
				image: BrunchBlossomEjemplo,
				webLink: "https://www.brunchblossom.com/",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.brunchblossom",
				iosLink:
					"https://apps.apple.com/us/app/brunch-blossom/id6745568150",
				hasApp: true,
			},
		],
	},
	{
		id: "shops",
		icon: ShoppingBag,
		examples: [
			{
				name: "Aurea Store",
				description: "shops_aurea",
				image: AureaEjemplo,
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.aurea",
				iosLink:
					"https://apps.apple.com/us/app/aurea-moda/id6742684349",
				hasApp: true,
			},
			{
				name: "Lía Joyas",
				description: "shops_lia",
				image: LiaEjemplo,
				webLink: "https://web.applaby.com/lia-joyas",
				androidLink:
					"https://play.google.com/store/apps/details?id=com.applaby.lia",
				iosLink:
					"https://apps.apple.com/us/app/lia/id6745760514",
				hasApp: true,
			},
			{
				name: "Pets & Co",
				description: "shops_pets",
				image: PetsEjemplo,
				webLink: "https://web.applaby.com/pets-co",
			},
		],
	},
];

const CREATOR_URL = "https://creator.applaby.com";

export default function Portfolio() {
	const { t } = useTranslation();
	const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);

	const scrollToCategory = (id) => {
		const el = document.getElementById(`portfolio-${id}`);
		if (!el) return;
		const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
		window.scrollTo({ top, behavior: "smooth" });
		setActiveCategory(id);
	};

	useEffect(() => {
		const onScroll = () => {
			let current = CATEGORIES[0].id;
			for (const category of CATEGORIES) {
				const el = document.getElementById(`portfolio-${category.id}`);
				if (!el) continue;
				const top = el.getBoundingClientRect().top;
				if (top - SCROLL_OFFSET - 40 <= 0) {
					current = category.id;
				}
			}
			setActiveCategory(current);
		};

		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	return (
		<div className="overflow-x-hidden bg-white">
			<Helmet>
				<title>{t("portfolio.seo.title")}</title>
				<meta
					name="description"
					content={t("portfolio.seo.description")}
				/>
				<link rel="canonical" href="https://applaby.com/ejemplos" />
				<meta property="og:title" content={t("portfolio.seo.title")} />
				<meta
					property="og:description"
					content={t("portfolio.seo.description")}
				/>
				<meta property="og:url" content="https://applaby.com/ejemplos" />
				<meta property="og:type" content="website" />
			</Helmet>

			<Header />

			{/* Hero */}
			<section className="pt-32 pb-6 sm:pt-40 sm:pb-8 px-6 text-center bg-gradient-to-b from-gray-50/80 to-white">
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					variants={fadeUp}
					className="max-w-3xl mx-auto"
				>
					<span className="inline-block text-primary text-sm font-semibold uppercase tracking-wider mb-4">
						{t("portfolio.hero.badge")}
					</span>
					<h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
						{t("portfolio.hero.title")}{" "}
						<span className="text-primary">
							{t("portfolio.hero.titleHighlight")}
						</span>
					</h1>
					<p className="mt-5 text-lg text-gray-600 max-w-xl mx-auto">
						{t("portfolio.hero.subtitle")}
					</p>
				</motion.div>

				{/* Category menu */}
				<motion.nav
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					variants={fadeUp}
					aria-label={t("portfolio.hero.sectors")}
					className="mt-8 sm:mt-10"
				>
					<div className="flex justify-center">
						<div className="flex max-w-full flex-wrap justify-center gap-2">
							{CATEGORIES.map((category) => {
								const Icon = category.icon;
								const isActive = activeCategory === category.id;
								return (
									<button
										key={category.id}
										type="button"
										onClick={() => scrollToCategory(category.id)}
										className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
											isActive
												? "border-primary bg-primary text-white shadow-[0_4px_14px_rgba(67,189,152,0.28)]"
												: "border-gray-200 bg-white text-gray-600 hover:border-primary/40 hover:text-primary"
										}`}
									>
										<Icon className="w-4 h-4" />
										{t(`portfolio.categories.${category.id}.title`)}
										<span
											className={`text-[11px] font-semibold ${
												isActive ? "text-white/80" : "text-gray-400"
											}`}
										>
											{category.examples.length}
										</span>
									</button>
								);
							})}
						</div>
					</div>
				</motion.nav>
			</section>

			{/* Categories */}
			{CATEGORIES.map((category, catIdx) => {
				const Icon = category.icon;
				return (
					<section
						key={category.id}
						id={`portfolio-${category.id}`}
						className={`px-6 ${
							catIdx === 0
								? "pt-6 pb-16 sm:pt-8 sm:pb-24"
								: "py-16 sm:py-24"
						} ${catIdx % 2 === 0 ? "bg-white" : "bg-gray-50/60"}`}
					>
						<div className="max-w-6xl mx-auto">
							{/* Category Header */}
							<motion.div
								initial="hidden"
								whileInView="visible"
								viewport={{ once: true }}
								variants={fadeUp}
								className="flex items-center gap-3 mb-8"
							>
								<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
									<Icon className="w-[18px] h-[18px] text-primary" />
								</div>
								<div>
									<h2 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
										{t(`portfolio.categories.${category.id}.title`)}
									</h2>
									<p className="text-gray-500 text-sm mt-0.5">
										{t(`portfolio.categories.${category.id}.subtitle`)}
									</p>
								</div>
							</motion.div>

							{/* Example Cards */}
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
								{category.examples.map((example, i) => (
									<motion.div
										key={example.name}
										custom={i}
										initial="hidden"
										whileInView="visible"
										viewport={{ once: true }}
										variants={fadeUp}
										className="group relative rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
									>
										{/* Preview area */}
										<div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center overflow-hidden">
											{example.image ? (
												example.webLink ? (
													<a
														href={example.webLink}
														target="_blank"
														rel="noopener noreferrer"
														className="absolute inset-0"
													>
														<img
															src={example.image}
															alt={example.name}
															className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
														/>
													</a>
												) : (
													<img
														src={example.image}
														alt={example.name}
														className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
													/>
												)
											) : example.webLink ? (
												<a
													href={example.webLink}
													target="_blank"
													rel="noopener noreferrer"
													className="absolute inset-0 flex items-center justify-center"
												>
													<div className="text-center">
														<div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white shadow-md flex items-center justify-center group-hover:scale-110 transition-transform">
															<span className="text-2xl font-bold text-primary">
																{example.name.charAt(0)}
															</span>
														</div>
														<span className="text-xs text-gray-400 flex items-center gap-1">
															<ExternalLink className="w-3 h-3" />
															{t("portfolio.tryDemo")}
														</span>
													</div>
												</a>
											) : (
												<div className="text-center">
													<div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white shadow-md flex items-center justify-center">
														<span className="text-2xl font-bold text-primary">
															{example.name.charAt(0)}
														</span>
													</div>
												</div>
											)}
											{/* Type badge */}
											<span
												className={`absolute top-3 right-3 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm backdrop-blur-sm ${
													example.hasApp
														? "bg-white text-primary ring-1 ring-black/5"
														: "bg-white text-gray-700 ring-1 ring-black/5"
												}`}
											>
												{example.hasApp ? "Web + App" : "Web"}
											</span>
										</div>

										{/* Info */}
										<div className="p-4 sm:p-5">
											<h3 className="text-base font-semibold text-gray-900">
												{example.name}
											</h3>
											<p className="text-[13px] text-gray-500 mt-1 mb-3 leading-relaxed">
												{t(
													`portfolio.descriptions.${example.description}`
												)}
											</p>

											<div className="flex flex-col gap-2">
												{example.webLink && (
													<a
														href={example.webLink}
														target="_blank"
														rel="noopener noreferrer"
														className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition"
													>
														<Globe className="w-4 h-4" />
														{t("portfolio.openWeb")}
													</a>
												)}
												{(example.iosLink || example.androidLink) && (
													<div className="flex flex-wrap gap-2">
														{example.iosLink && (
															<a
																href={example.iosLink}
																target="_blank"
																rel="noopener noreferrer"
																className="inline-flex flex-1 items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-600 text-[11px] font-medium hover:border-primary hover:text-primary transition"
															>
																<Apple className="w-3 h-3" />
																App Store
															</a>
														)}
														{example.androidLink && (
															<a
																href={example.androidLink}
																target="_blank"
																rel="noopener noreferrer"
																className="inline-flex flex-1 items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-600 text-[11px] font-medium hover:border-primary hover:text-primary transition"
															>
																<Play className="w-3 h-3" />
																Google Play
															</a>
														)}
													</div>
												)}
											</div>
										</div>
									</motion.div>
								))}
							</div>
						</div>
					</section>
				);
			})}

			{/* CTA */}
			<section className="py-20 sm:py-28 px-6 bg-gray-900 text-white text-center">
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					variants={fadeUp}
					className="max-w-2xl mx-auto"
				>
					<h2 className="text-2xl sm:text-4xl font-bold">
						{t("portfolio.cta.title")}
					</h2>
					<p className="mt-4 text-gray-300 text-lg">
						{t("portfolio.cta.subtitle")}
					</p>
					<a
						href={CREATOR_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-primary text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition"
					>
						{t("portfolio.cta.button")}
						<ArrowRight className="w-4 h-4" />
					</a>
					<p className="mt-3 text-sm text-gray-400">
						{t("portfolio.cta.note")}
					</p>
				</motion.div>
			</section>

			<Footer />
			<CookiesBanner />
		</div>
	);
}
